import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import Image from "next/legacy/image";
import Link from "@/utils/link";

import { ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/outline";

import { getMDXComponent } from "mdx-bundler/client";

import { processMdx } from "@/utils/mdx";
import HomeHeader from "@/components/home/HomeHeader";
import { fetchGraphql } from "@/utils/query";
import { imageLoader, removeExt } from "@/utils/image";

import Breadcrumbs from "@/components/common/Breadcrumbs";
import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";
import Button from "@/ui/Button";
import { ResourceLink } from "./index";
import ArticleCard from "@/components/article/ArticleCard";
import HomeSearch from "@/components/home/HomeSearch";

const CustomLink = (props) => {
    const href = props.href;
    const isInternalLink = href && (href.startsWith("/") || href.startsWith("#"));

    if (isInternalLink) {
        return (
            <Link href={href}>
                <a {...props}>{props.children}</a>
            </Link>
        );
    }

    return <a target="_blank" rel="noopener noreferrer" {...props} />;
};

const MDXComponents = {
    Image,
    a: CustomLink,
};

const GlossaryPage = ({ title, sections, articles }) => {
    const router = useRouter();
    const [openAll, setOpenAll] = useState(0);
    const [closeAll, setCloseAll] = useState(0);

    const handleClickLetter = (id) => {
        const e = document.getElementById(id);

        if (e) {
            const yOffset = -136;

            const y = e.getBoundingClientRect().top + window.pageYOffset + yOffset;

            // console.log(
            //     "scroll",
            //     e.getBoundingClientRect().top,
            //     window.pageYOffset,
            //     yOffset,
            //     y
            // );

            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    const data = {
        A: [],
        B: [],
        C: [],
        D: [],
        E: [],
        F: [],
        G: [],
        H: [],
        I: [],
        J: [],
        K: [],
        L: [],
        M: [],
        N: [],
        O: [],
        P: [],
        Q: [],
        R: [],
        S: [],
        T: [],
        U: [],
        V: [],
        W: [],
        X: [],
        Y: [],
        Z: [],
    };

    sections.forEach((section) => {
        const c = section.heading.trim().toUpperCase().substring(0, 1);
        data[c].push(section);
    });

    return (
        <div className="h-screen bg-white">
            <HomeHeader fixed />

            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16">
                <Breadcrumbs
                    links={[
                        { name: "Resources", href: "/resources" },
                        { name: "Glossary", href: "/resources/glossary" },
                    ]}
                />
            </div>

            <div>
                <div className="hidden sm:block">
                    <div className="relative w-full h-40 sm:h-72 bg-black">
                        <Image
                            loader={imageLoader}
                            src="https://cdn.purpledoorfinders.com/purpledoorfinders/purpledoorfinders-help"
                            alt="purple-door-finders-glossary"
                            layout="fill"
                            objectFit="cover"
                            objectPosition="0px -200px"
                            priority
                            unoptimized={false}
                            className="opacity-80"
                        />

                        <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16">
                            <h1 className="absolute top-12 sm:top-16 text-3xl sm:text-5xl text-white font-headline font-bold">
                                Glossary
                            </h1>
                            <div className="absolute top-24 sm:top-36 w-full">
                                <HomeSearch
                                    inputClassName="w-[300px] sm:w-[500px]"
                                    enableLocation={true}
                                    higherInput={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sm:hidden">
                    <h1 className="px-4 text-3xl text-primary-500 font-headline font-bold">
                        Glossary
                    </h1>
                    <div className="mt-4 px-4 py-4 bg-gray-300">
                        <HomeSearch
                            inputClassName="w-[calc(100%-64px)] max-w-[500px]"
                            enableLocation={true}
                            higherInput={false}
                        />
                    </div>
                </div>
            </div>

            <div className="pt-8 pb-8 container mx-auto px-4 sm:px-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-16">
                    <div className="md:col-span-2 space-y-2">
                        <div className="sticky z-10 top-12 sm:top-16 mb-4 -ml-2 py-2 flex flex-wrap bg-white">
                            {Object.keys(data).map((c) => {
                                if (data[c].length === 0) {
                                    return (
                                        <span
                                            key={c}
                                            className="p-2 text-gray-300 text-xl font-headline font-bold"
                                        >
                                            {c}
                                        </span>
                                    );
                                } else {
                                    return (
                                        <button
                                            key={c}
                                            className="p-2 text-primary-500 text-xl font-headline font-bold rounded-full hover:bg-gray-200"
                                            onClick={() =>
                                                handleClickLetter("glossary-" + c)
                                            }
                                        >
                                            {c}
                                        </button>
                                    );
                                }
                            })}
                        </div>

                        <div>
                            {(() => {
                                // Flatten all sections into a single array
                                const allSections = [];
                                Object.keys(data)
                                    .filter((c) => data[c].length > 0)
                                    .forEach((c) => {
                                        data[c].forEach((section) => {
                                            allSections.push(section);
                                        });
                                    });

                                let itemCounter = 0;

                                return Object.keys(data)
                                    .filter((c) => data[c].length > 0)
                                    .map((c) => (
                                        <div key={c} id={"glossary-" + c}>
                                            <h3 className="text-primary-500 text-2xl font-headline font-bold">
                                                {c}
                                            </h3>
                                            <div className="mt-8">
                                                {data[c].map((section) => {
                                                    itemCounter++;
                                                    const showSearch =
                                                        itemCounter % 5 === 0 &&
                                                        itemCounter < allSections.length;

                                                    return (
                                                        <React.Fragment
                                                            key={section.heading}
                                                        >
                                                            <Glossary section={section} />
                                                            {showSearch && (
                                                                <div className="my-6 py-6 bg-gray-300 px-4">
                                                                    <HomeSearch
                                                                        inputClassName="w-[calc(100%-64px)] max-w-[500px]"
                                                                        enableLocation={
                                                                            true
                                                                        }
                                                                        higherInput={false}
                                                                    />
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ));
                            })()}
                        </div>
                    </div>

                    <div className="mt-8 sm:mt-0">
                        <h3 className="text-xl text-primary-500 font-headline font-bold">
                            Related Resources
                        </h3>

                        <div className="mt-4 w-full flex flex-col space-y-4 ">
                            <ResourceLink
                                title="Senior Living Guidebooks"
                                href="/resources/guidebooks"
                            />

                            <ResourceLink
                                title="Senior Living Glossary"
                                href="/resources/glossary"
                            />

                            <ResourceLink
                                title="Frequently Asked Questions"
                                href="/resources/faq"
                            />

                            <Link href="/resources">
                                <a className="text-primary-500 text-sm uppercase font-black hover:underline">
                                    See All Resources
                                </a>
                            </Link>
                        </div>

                        <div className="mt-8 sm:flex-shrink-0 sm:w-72">
                            <h3 className="text-xl text-primary-500 font-headline font-bold">
                                Recent Articles
                            </h3>

                            <div className="mt-4 flex flex-col space-y-8">
                                {articles
                                    ? articles.map((a, index) => (
                                          <ArticleCard
                                              key={a ? a.title : index}
                                              article={a}
                                          />
                                      ))
                                    : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="z-10">
                <SeoLinks />
                <HomeFooter />
            </div>
        </div>
    );
};

const Glossary = ({ section }) => {
    const { code } = section;
    const Component = useMemo(() => getMDXComponent(code), [code]);

    return (
        <div>
            <h4 className="text-primary-500 text-lg font-headline font-bold">
                {section.heading}
            </h4>
            <div className="mt-4 mb-8 prose max-w-3xl">
                <Component components={MDXComponents} />
            </div>
        </div>
    );
};

const Section = ({ section, openAll, closeAll }) => {
    const [open, setOpen] = useState(openAll > 0);

    //console.log("all", open, openAll, closeAll);

    useEffect(() => {
        if (openAll > 0) {
            setOpen(true);
        } else if (closeAll > 0) {
            setOpen(false);
        }
    }, [openAll, closeAll]);

    const { code } = section;
    const Component = useMemo(() => getMDXComponent(code), [code]);

    return (
        <div>
            <button
                onClick={() => setOpen((x) => !x)}
                className="w-full flex items-center pl-1 pr-4 py-2 text-left text-primary-500 bg-gray-300 hover:bg-gray-200"
            >
                <div className="h-6">
                    {open ? (
                        <ChevronDownIcon className="w-6 h-6 mr-2" />
                    ) : (
                        <ChevronRightIcon className="w-6 h-6 mr-2" />
                    )}
                </div>
                <span className="font-headline font-bold text-lg">{section.heading}</span>
            </button>

            {open ? (
                <div className="pl-8 pr-4 mb-8 prose max-w-3xl">
                    <Component components={MDXComponents} />
                </div>
            ) : null}
        </div>
    );
};

const SideLink = ({ children, href }) => {
    return (
        <Link href={href}>
            <a className="w-full pt-2 text-md font-bold hover:underline">{children}</a>
        </Link>
    );
};

export const getStaticProps = async () => {
    const result = await fetchGraphql(
        `query($find: JSON, $findArticle: JSON, $sortArticle: JSON) {
            pages(find: $find) {
                pageId
                title
                secondaryHeading
                content                
            }
            articles(find: $findArticle, limit: 3, sort: $sortArticle) {
                articleId
                type
                newsroomType
                title
                date
                description
                mainImageUrl
                keywords
                topic
                author_link {
                    name
                }
            }
        }
    `,
        {
            find: {
                pageId: "glossary",
            },
            findArticle: {
                newsroomType: { $nin: ["Newsroom", "Endorsement"] },
            },
            sortArticle: { _updatedAt: -1 },
        }
    );

    let page = null;
    let articles = [];

    if (result && result.data && result.data.pages && result.data.pages.length > 0) {
        page = result.data.pages[0];
        articles = result.data.articles;
        //console.log("page", page);
    } else {
        return {
            props: {
                title: "glossary",
                sections: [],
            },
        };
    }

    let sections = [];
    if (page.content) {
        const f = page.content.split("##");

        for (let i = 1; i < f.length; i++) {
            const lines = f[i].split("\n");
            const heading = lines[0];
            const content = lines.filter((_, index) => index > 0).join("\n");
            const post = await processMdx(
                content.replaceAll("\n", "  \n").replaceAll("<br>", "  \n")
            );
            sections.push({ heading, ...post });
        }
    }

    return {
        props: {
            title: page.title,
            sections,
            articles,
            meta: {
                title: page.title + " | Purple Door",
                description: page.secondaryHeading,
            },
        },
    };
};

export default GlossaryPage;
